import { Box, Skeleton, Stack, Typography } from '@mui/material';
import React from 'react';
import { WhatsappEditTemplate } from 'types/templates';
import useLocalization from 'hooks/useLocalization';
import TemplatePreview from 'views/common/templates/single-template/TemplatePreview';
import { buildTemplatePreviewVarsData } from '../buildTemplatePreviewVarsData';

interface TemplatePreviewSidebarProps {
  selectedTemplate: WhatsappEditTemplate | null;
  /** While fetching templates from Meta — show skeleton + loading copy */
  isLoadingTemplates?: boolean;
}

const TemplatePreviewSidebar: React.FC<TemplatePreviewSidebarProps> = ({
  selectedTemplate,
  isLoadingTemplates = false,
}) => {
  const { currentLocale } = useLocalization();
  const localization = React.useMemo(() => currentLocale.settingsPopup.templates, [currentLocale]);

  const varsData = React.useMemo(
    () => buildTemplatePreviewVarsData(selectedTemplate),
    [selectedTemplate],
  );

  const hebrewDisplayName = selectedTemplate?.niceName?.trim();
  const technicalName = selectedTemplate?.name?.trim();

  if (isLoadingTemplates) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600} textAlign="center">
          {localization.list.preview.loadingMetaTemplates}
        </Typography>
        <Skeleton variant="text" width="70%" height={28} animation="wave" />
        <Skeleton variant="text" width="50%" height={22} animation="wave" />
        <Stack spacing={1} sx={{ flex: 1, mt: 1 }}>
          <Skeleton variant="rounded" height={120} animation="wave" sx={{ borderRadius: 2 }} />
          <Skeleton variant="rounded" height={48} animation="wave" />
          <Skeleton variant="rounded" height={36} animation="wave" />
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {hebrewDisplayName ? (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            mb: 0.25,
            '&:hover .MuiIconButton-root': {
              opacity: 1,
            },
            display: 'inline-block',
          }}
        >
          {hebrewDisplayName}
        </Typography>
      ) : null}

      <Typography
        variant="body2"
        sx={{ wordBreak: 'break-all', mb: 2.5 }}
        fontSize={13}
        color={hebrewDisplayName ? 'text.secondary' : 'text.primary'}
        fontWeight={hebrewDisplayName ? 400 : 700}
      >
        {technicalName ||
          (selectedTemplate
            ? localization.list.preview.nameDefaultValue
            : localization.list.preview.sidebarHintSelectRow)}
      </Typography>

      {selectedTemplate && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TemplatePreview
            originalTemplate={selectedTemplate}
            varsData={varsData}
            noUpload={true}
            whatsappStyle
          />
        </Box>
      )}
    </Box>
  );
};

export default TemplatePreviewSidebar;
